// components/NetWorthCard.tsx

import React from 'react';
import { AccountTotal, sumTotals, extractCreditScore } from '../services/totalsService';
import NetWorthGraph from './NetWorthGraph';

interface NetWorthCardProps {
  totals: AccountTotal[];
  loading: boolean;
  error: string | null;
}

function formatNetWorth(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

function creditScoreLabel(score: number): { label: string; pct: number } {
  if (score >= 750) return { label: 'Excellent', pct: 90 };
  if (score >= 700) return { label: 'Very Good', pct: 75 };
  if (score >= 670) return { label: 'Good',      pct: 60 };
  if (score >= 580) return { label: 'Fair',       pct: 40 };
  return               { label: 'Poor',       pct: 20 };
}

const NetWorthCard: React.FC<NetWorthCardProps> = ({ totals, loading, error }) => {
  const netWorth   = sumTotals(totals);
  const creditAcct = extractCreditScore(totals);
  const credit     = creditAcct ? creditScoreLabel(creditAcct.total) : null;

  return (
    <div className="networth-card">
      {loading && (
        <div className="state-box">
          <div className="spinner" />
          Loading…
        </div>
      )}

      {!loading && error && (
        <div className="state-box" style={{ color: 'rgba(231,76,60,0.8)' }}>
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* ── Row 1: Net Worth + Credit Score ── */}
          <div className="nw-top-row">
            <div>
              <div className="networth-label">Net Worth</div>
              <div className="networth-value">{formatNetWorth(netWorth)}</div>
            </div>

            {creditAcct && credit && (
              <div className="nw-credit-widget">
                <div className="nw-credit-label">Credit Score</div>
                <div className="nw-credit-score">{Math.round(creditAcct.total)}</div>
                <div className="nw-credit-bar-track">
                  <div
                    className="nw-credit-bar-fill"
                    style={{ width: `${credit.pct}%` }}
                  />
                </div>
                <div className="nw-credit-rating">{credit.label}</div>
              </div>
            )}
          </div>

          {/* ── Divider ── */}
          <div className="nw-divider" />

          {/* ── Row 2: Graph ── */}
          <NetWorthGraph netWorth={netWorth} />
        </>
      )}
    </div>
  );
};

export default NetWorthCard;
